import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as api from '../../api/AccountsAPI/verifyChequeAPI';

const err = (e, fallback) => e?.ErrorDetails?.ErrorMessage || e?.Message || e?.message || fallback;

export const fetchVerifyChequeQueue = createAsyncThunk(
    'verifyCheque/fetchQueue',
    async (roleId, { rejectWithValue }) => {
        try { return await api.getVerificationCheques(roleId); }
        catch (e) { return rejectWithValue(err(e, 'Failed to fetch cheque book verification queue')); }
    }
);

export const fetchVerifyChequeDetail = createAsyncThunk(
    'verifyCheque/fetchDetail',
    async (chequeId, { rejectWithValue }) => {
        try { return await api.getVerificationChequeById(chequeId); }
        catch (e) { return rejectWithValue(err(e, 'Failed to fetch cheque book details')); }
    }
);

export const fetchReturnedChequeDetail = createAsyncThunk(
    'verifyCheque/fetchReturnedDetail',
    async (chequeId, { rejectWithValue }) => {
        try { return await api.getReturnedChequeById(chequeId); }
        catch (e) { return rejectWithValue(err(e, 'Failed to load the returned cheque book')); }
    }
);

export const fetchChequeBanks = createAsyncThunk(
    'verifyCheque/fetchBanks',
    async (_, { rejectWithValue }) => {
        try { return await api.getBanks(); }
        catch (e) { return rejectWithValue(err(e, 'Failed to fetch banks')); }
    }
);

export const submitVerifyCheque = createAsyncThunk(
    'verifyCheque/verify',
    async (payload, { rejectWithValue }) => {
        try { return await api.verifyCheque(payload); }
        catch (e) { return rejectWithValue(err(e, 'Error Occurred While Cheque Book Verification')); }
    }
);

export const submitUpdateCheque = createAsyncThunk(
    'verifyCheque/update',
    async (payload, { rejectWithValue }) => {
        try { return await api.updateReturnedCheque(payload); }
        catch (e) { return rejectWithValue(err(e, 'Error Occurred While Adding Cheque Nos.')); }
    }
);

const initialState = {
    queue: [], queueLoading: false, queueError: null,
    detail: null, detailLoading: false, detailError: null,
    banks: [],
    verifyLoading: false, updateLoading: false,
};

// The pending-detail endpoint returns a one-row list; the returned-detail endpoint a single object.
const firstRow = (data) => (Array.isArray(data) ? data[0] || null : data || null);

const verifyChequeSlice = createSlice({
    name: 'verifyCheque',
    initialState,
    reducers: {
        clearVerifyChequeDetail: (s) => { s.detail = null; s.detailError = null; s.detailLoading = false; },
        resetVerifyCheque: () => initialState,
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchVerifyChequeQueue.pending, (s) => { s.queueLoading = true; s.queueError = null; })
            .addCase(fetchVerifyChequeQueue.fulfilled, (s, a) => { s.queueLoading = false; s.queue = a.payload?.Data || []; })
            .addCase(fetchVerifyChequeQueue.rejected, (s, a) => { s.queueLoading = false; s.queueError = a.payload; s.queue = []; });

        [fetchVerifyChequeDetail, fetchReturnedChequeDetail].forEach((thunk) => {
            builder
                .addCase(thunk.pending, (s) => { s.detailLoading = true; s.detailError = null; s.detail = null; })
                .addCase(thunk.fulfilled, (s, a) => { s.detailLoading = false; s.detail = firstRow(a.payload?.Data); })
                .addCase(thunk.rejected, (s, a) => { s.detailLoading = false; s.detailError = a.payload; s.detail = null; });
        });

        builder
            .addCase(fetchChequeBanks.fulfilled, (s, a) => { s.banks = a.payload?.Data || []; })
            .addCase(fetchChequeBanks.rejected, (s) => { s.banks = []; });

        builder
            .addCase(submitVerifyCheque.pending, (s) => { s.verifyLoading = true; })
            .addCase(submitVerifyCheque.fulfilled, (s) => { s.verifyLoading = false; })
            .addCase(submitVerifyCheque.rejected, (s) => { s.verifyLoading = false; });

        builder
            .addCase(submitUpdateCheque.pending, (s) => { s.updateLoading = true; })
            .addCase(submitUpdateCheque.fulfilled, (s) => { s.updateLoading = false; })
            .addCase(submitUpdateCheque.rejected, (s) => { s.updateLoading = false; });
    },
});

export const { clearVerifyChequeDetail, resetVerifyCheque } = verifyChequeSlice.actions;

export const selectVCQueue = (s) => s.verifyCheque.queue;
export const selectVCQueueLoading = (s) => s.verifyCheque.queueLoading;
export const selectVCQueueError = (s) => s.verifyCheque.queueError;
export const selectVCDetail = (s) => s.verifyCheque.detail;
export const selectVCDetailLoading = (s) => s.verifyCheque.detailLoading;
export const selectVCDetailError = (s) => s.verifyCheque.detailError;
export const selectVCBanks = (s) => s.verifyCheque.banks;
export const selectVCVerifyLoading = (s) => s.verifyCheque.verifyLoading;
export const selectVCUpdateLoading = (s) => s.verifyCheque.updateLoading;

export default verifyChequeSlice.reducer;
